import React from 'react';
import { Button, Col, Form, Row, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { I18n } from '@aws-amplify/core';

import { Text } from '@/components/Typography';
import { fieldsUnitTexts } from './utils/FieldsUnitTexts';
import { DependentRadioField } from './DependentRadioField';
import { DependentNumberField } from './DependentNumberField';
import { SelectDependentInputsList } from './SelectDependentInputsList';
import { ConditionCard } from '@/components/views/EwaJdsD86/Report/components/ConditionCard';
import { FieldKey, useSelectDependentInputs } from './_hooks/useSelectDependentInputs';
import type { AditionalItemProps } from './types';

const { formatLabelWithUnit, formatPlaceholderWithUnit } = fieldsUnitTexts;

export function SelectDependentInputs({ additionalItem }: Readonly<AditionalItemProps>): JSX.Element {
	const { id, title_1, title_2, title_3, title_4, title_5, title_6, unit_1, unit_2, unit_4, unit_5, unit_6 } =
		additionalItem;

	const {
		formFieldName,
		localAdditionalItem,
		selectOptions,
		radioOptions,
		isOptionsLoading,
		isFieldVisible,
		filterOption,
		handleChangeSelect,
		handleClearSelect,
		handleAddAdditionalItemValueClick,
		handleRemoveAdditionalItemClick,
		isAddDisabled,
		hasAddedValues,
		worstRisk
	} = useSelectDependentInputs({ additionalItem });

	return (
		<Row gutter={[0, 15]} key={id}>
			<Col span={24}>
				<Row gutter={[0, 5]}>
					<Col span={24}>
						<Text strong>{formatLabelWithUnit(title_1, unit_1)}</Text>
					</Col>
					<Col xs={18} xxl={18}>
						<Form.Item
							style={{ margin: 0 }}
							name={[...formFieldName, 'additional_items', id, 'additional_item_option_id_1']}
						>
							<Select
								showSearch
								allowClear
								loading={isOptionsLoading}
								disabled={isOptionsLoading}
								filterOption={filterOption}
								onClear={handleClearSelect}
								onChange={handleChangeSelect}
								placeholder={I18n.get(title_1)}
								options={selectOptions.map(({ id: optionId, description }) => ({
									label: `${I18n.get(description)}`,
									value: optionId
								}))}
							/>
						</Form.Item>
					</Col>
				</Row>
			</Col>

			{isFieldVisible(FieldKey.GLOVE_TYPE) && (
				<Col span={24}>
					<DependentRadioField
						label={I18n.get(title_3)}
						options={radioOptions}
						name={[...formFieldName, 'additional_items', id, 'additional_item_option_id_2']}
					/>
				</Col>
			)}

			{isFieldVisible(FieldKey.OBJECT_DIMENSION) && (
				<Col span={24}>
					<DependentNumberField
						xs={18}
						xxl={18}
						label={formatLabelWithUnit(title_2, unit_2)}
						placeholder={formatPlaceholderWithUnit('0', unit_2)}
						name={[...formFieldName, 'additional_items', id, 'value_1']}
					/>
				</Col>
			)}

			<Col span={24}>
				<Row align="bottom" gutter={[14, 20]}>
					{isFieldVisible(FieldKey.HEIGHT) && (
						<Col span={7}>
							<DependentNumberField
								label={formatLabelWithUnit(title_4, unit_4)}
								placeholder={formatPlaceholderWithUnit('0', unit_4)}
								name={[...formFieldName, 'additional_items', id, 'value_2']}
							/>
						</Col>
					)}

					{isFieldVisible(FieldKey.WIDTH) && (
						<Col span={7}>
							<DependentNumberField
								label={formatLabelWithUnit(title_5, unit_5)}
								placeholder={formatPlaceholderWithUnit('0', unit_5)}
								name={[...formFieldName, 'additional_items', id, 'value_3']}
							/>
						</Col>
					)}

					{isFieldVisible(FieldKey.DIAMETER) && (
						<Col span={7}>
							<DependentNumberField
								label={formatLabelWithUnit(title_6, unit_6)}
								placeholder={formatPlaceholderWithUnit('0', unit_6)}
								name={[...formFieldName, 'additional_items', id, 'value_4']}
							/>
						</Col>
					)}

					<Col>
						<Button
							shape="circle"
							type="primary"
							icon={<PlusOutlined />}
							onClick={handleAddAdditionalItemValueClick}
							disabled={isAddDisabled}
						/>
					</Col>
				</Row>
			</Col>

			{hasAddedValues && (
				<Col span={24}>
					<SelectDependentInputsList
						items={localAdditionalItem}
						radioOptions={radioOptions}
						onDeleteValue={handleRemoveAdditionalItemClick}
					/>
				</Col>
			)}

			<Col span={24}>
				<ConditionCard result={worstRisk} isLoading={isOptionsLoading} />
			</Col>
		</Row>
	);
}
